import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { createContext, useMemo, useState } from 'react';
import DashboardLayout from './components/DashboardLayout';
import StockLensesPage from './pages/StockLensesPage';
import CommandesPage from './pages/CommandesPage';
import SavPage from './pages/SavPage';
import SitesPage from './pages/SitesPage';
import CustomersPage from './pages/CustomersPage';
import UsersPage from './pages/UsersPage';
import RolesPage from './pages/RolesPage';
import StatisticsPage from './pages/StatisticsPage';
import DelayStatisticsPage from './pages/DelayStatisticsPage';
import DelayStatisticsV2Page from './pages/DelayStatisticsV2Page';
import StockMovementStatisticsPage from './pages/StockMovementStatisticsPage';
import StatsPerformancePage from './pages/StatsPerformancePage';
import LogisticsStatisticsPage from './pages/LogisticsStatisticsPage';
import LogisticsOrdersPage from './pages/LogisticsOrdersPage';
import StockOrdersPage from './pages/StockOrdersPage';
import StockReservationsPage from './pages/StockReservationsPage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierAccountsPage from './pages/SupplierAccountsPage';
import SettingsPage from './pages/SettingsPage';
import LogisticsSupplierBrandsPage from './pages/LogisticsSupplierBrandsPage';
import LoginPage from './pages/LoginPage';
import ActivityJournalPage from './pages/ActivityJournalPage';
import EmailConfirmationPage from './pages/EmailConfirmationPage';
import { SiteSessionProvider, useSiteSession } from './context/SiteSessionContext';
import { AuthProvider, useAuth } from './context/AuthContext';

export const ColorModeContext = createContext({
  mode: 'dark',
  toggleColorMode: () => {},
});

const COLOR_MODE_STORAGE_KEY = 'opticdev.colorMode';

function readStoredColorMode() {
  try {
    const storedMode = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    return storedMode === 'light' || storedMode === 'dark' ? storedMode : 'dark';
  } catch (_error) {
    return 'dark';
  }
}

function buildTheme(mode) {
  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#66b2ff',
      },
      background: {
        default: mode === 'dark' ? '#03070d' : '#f4f7fb',
        paper: mode === 'dark' ? '#0d1117' : '#ffffff',
      },
      divider: mode === 'dark' ? '#1f2937' : '#e2e8f0',
      text: {
        primary: mode === 'dark' ? '#f8fafc' : '#0f172a',
        secondary: mode === 'dark' ? '#9ca3af' : '#64748b',
      },
    },
    shape: {
      borderRadius: 14,
    },
    typography: {
      fontFamily: [
        'Montserrat',
        '-apple-system',
        'BlinkMacSystemFont',
        'SF Pro Display',
        'SF Pro Text',
        'Segoe UI',
        'Roboto',
        'Helvetica Neue',
        'Arial',
        'sans-serif',
      ].join(','),
      button: {
        textTransform: 'none',
        fontWeight: 800,
      },
    },
  });
}

function AllowedPage({ roles = [], permission, children }) {
  const { isAdmin, siteType, hasPermission } = useSiteSession();
  const siteAllowed = roles.includes(siteType);
  const permissionAllowed = permission ? hasPermission(permission) : false;

  if (!isAdmin && !siteAllowed && !permissionAllowed) {
    return <Navigate to={siteType === 'sav' ? '/sav' : '/suivi-atelier'} replace />;
  }
  return children;
}

function HomeRedirect() {
  const { siteType } = useSiteSession();
  return <Navigate to={siteType === 'sav' ? '/sav' : '/suivi-atelier'} replace />;
}

function SettingsEntryPage() {
  const { isAdmin } = useSiteSession();
  return isAdmin ? <SettingsPage /> : <Navigate to="/settings/logistique/marques" replace />;
}

function ProtectedAccountRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/auth" replace />;
}

function App() {
  const [mode, setMode] = useState(readStoredColorMode);

  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () =>
        setMode((currentMode) => {
          const nextMode = currentMode === 'light' ? 'dark' : 'light';
          try {
            localStorage.setItem(COLOR_MODE_STORAGE_KEY, nextMode);
          } catch (_error) {}
          return nextMode;
        }),
    }),
    [mode]
  );

  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <SiteSessionProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/auth" element={<LoginPage />} />
                <Route path="/confirmation-email" element={<EmailConfirmationPage />} />
                <Route path="/connexion" element={<Navigate to="/auth" replace />} />
                <Route
                  element={
                    <ProtectedAccountRoute>
                      <DashboardLayout />
                    </ProtectedAccountRoute>
                  }
                >
                  <Route index element={<HomeRedirect />} />
                  <Route path="/stock/verres" element={<AllowedPage roles={['store', 'workshop']}><StockLensesPage /></AllowedPage>} />
                  <Route path="/stock/suivi" element={<Navigate to="/suivi-atelier" replace />} />
                  <Route path="/stock/reservations" element={<AllowedPage roles={['store', 'workshop']}><StockReservationsPage /></AllowedPage>} />
                  <Route path="/stock/commandes" element={<AllowedPage roles={['workshop']}><StockOrdersPage /></AllowedPage>} />
                  <Route path="/stock/reassort" element={<AllowedPage roles={['workshop']}><StockOrdersPage /></AllowedPage>} />
                  <Route path="/stock/bl" element={<Navigate to="/suivi-atelier/bl" replace />} />
                  <Route path="/commandes" element={<Navigate to="/suivi-atelier" replace />} />
                  <Route path="/suivi-atelier" element={<CommandesPage viewMode="nonStock" />} />
                  <Route path="/suivi-atelier/bl" element={<CommandesPage viewMode="nonStock" dispatchOnly />} />
                  <Route path="/sav" element={<AllowedPage roles={['store', 'sav']}><SavPage /></AllowedPage>} />
                  <Route path="/sav/documents" element={<AllowedPage roles={['store', 'sav']}><SavPage documentsOnly /></AllowedPage>} />
                  <Route path="/sav-commandes" element={<AllowedPage roles={['sav']}><LogisticsOrdersPage /></AllowedPage>} />
                  <Route path="/statistiques" element={<AllowedPage roles={['workshop']} permission="VIEW_STATISTICS"><StatisticsPage /></AllowedPage>} />
                  <Route path="/statistiques/delais" element={<AllowedPage roles={['workshop']} permission="VIEW_STATISTICS"><DelayStatisticsPage /></AllowedPage>} />
                  <Route path="/statistiques/delais-v2" element={<AllowedPage roles={[]}><DelayStatisticsV2Page /></AllowedPage>} />
                  <Route path="/statistiques/performance" element={<AllowedPage roles={[]}><StatsPerformancePage /></AllowedPage>} />
                  <Route path="/statistiques/stock" element={<AllowedPage roles={['workshop']} permission="VIEW_STATISTICS"><StockMovementStatisticsPage /></AllowedPage>} />
                  <Route path="/statistiques/logistique" element={<AllowedPage roles={['sav']} permission="VIEW_STATISTICS"><LogisticsStatisticsPage /></AllowedPage>} />
                  <Route path="/sites" element={<AllowedPage roles={[]}><SitesPage /></AllowedPage>} />
                  <Route path="/fournisseurs" element={<AllowedPage roles={[]} permission="MANAGE_LOGISTICS_SUPPLIERS"><SuppliersPage /></AllowedPage>} />
                  <Route path="/comptes-fournisseurs" element={<AllowedPage roles={[]} permission="MANAGE_LOGISTICS_SUPPLIERS"><SupplierAccountsPage /></AllowedPage>} />
                  <Route path="/settings/logistique/marques" element={<AllowedPage roles={[]} permission="MANAGE_LOGISTICS_SUPPLIERS"><LogisticsSupplierBrandsPage /></AllowedPage>} />
                  <Route path="/clients" element={<AllowedPage roles={['store']}><CustomersPage /></AllowedPage>} />
                  <Route path="/utilisateurs" element={<AllowedPage roles={[]}><UsersPage /></AllowedPage>} />
                  <Route path="/roles" element={<AllowedPage roles={[]}><RolesPage /></AllowedPage>} />
                  <Route path="/settings" element={<AllowedPage roles={[]} permission="MANAGE_LOGISTICS_SUPPLIERS"><SettingsEntryPage /></AllowedPage>} />
                  <Route path="/journal" element={<AllowedPage roles={[]} permission="VIEW_ACTIVITY_LOG"><ActivityJournalPage /></AllowedPage>} />
                </Route>
              </Routes>
            </BrowserRouter>
          </SiteSessionProvider>
        </AuthProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
